/**
 * AccumulationChart Tooltip file.
 */
import { Browser, remove } from '@syncfusion/ej2-base';
import { isNullOrUndefined } from '@syncfusion/ej2-base';
import { AccPointData, withInBounds, indexFinder } from '../../common/utils/helper';
//import { Rect } from '@syncfusion/ej2-svg-base';
import { BaseTooltip } from '../../common/user-interaction/tooltip';
import { tooltipRender } from '../../common/model/constants';
/**
 * The `AccumulationTooltip` module is used to render tooltips for the accumulation chart.
 */
export class AccumulationTooltip extends BaseTooltip {
    constructor(accumulation) {
        super(accumulation);
        this.accumulation = accumulation;
        this.addEventListener();
        this.template = this.accumulation.tooltip.template;
    }
    /**
     * Adds an event listener.
     *
     * @hidden
     * @returns {void}
     */
    addEventListener() {
        if (this.accumulation.isDestroyed) {
            return;
        }
        this.accumulation.on(Browser.isPointer ? 'pointerleave' : 'mouseleave', this.mouseLeaveHandler, this);
        this.accumulation.on(Browser.touchMoveEvent, this.mouseMoveHandler, this);
        this.accumulation.on(Browser.touchEndEvent, this.mouseUpHandler, this);
    }
    mouseLeaveHandler() {
        this.removeTooltip(this.accumulation.tooltip.fadeOutDuration);
    }
    mouseUpHandler(e) {
        const control = this.accumulation;
        if (control.tooltip.enable && control.isTouch && withInBounds(control.mouseX, control.mouseY, control.initialClipRect)) {
            this.tooltip(e);
            this.removeTooltip(2000);
        }
    }
    mouseMoveHandler(e) {
        const control = this.accumulation;
        // Tooltip for chart series.
        if (control.tooltip.enable && withInBounds(control.mouseX, control.mouseY, control.initialClipRect)) {
            this.tooltip(e);
        }
    }
    /**
     * Renders the tooltip.
     *
     * @param {PointerEvent | TouchEvent} event - The mouse move event or touch event.
     * @returns {void}
     * @private
     */
    tooltip(event) {
        this.renderSeriesTooltip(this.accumulation, this.getPieData(event, this.accumulation));
    }
    /**
     * @private
     */
    renderSeriesTooltip(chart, data) {
        const svgElement = this.getElement(this.element.id + '_tooltip_svg');
        const isTooltip = svgElement && parseInt(svgElement.getAttribute('opacity'), 10) > 0;
        const tooltipDiv = this.getTooltipElement(isTooltip);
        const isFirst = !isTooltip;
        this.currentPoints = [];
        if (data.point && (!this.previousPoints[0] || (this.previousPoints[0].point !== data.point
            || this.accumulation.tooltip.followPointer))) {
            if (!this.accumulation.tooltip.followPointer && (this.previousPoints[0]
                && data.point.index === this.previousPoints[0].point.index
                && data.series.index === this.previousPoints[0].series.index)) {
                return null;
            }
            if (this.pushData(data, isFirst, tooltipDiv, false)) {
                this.triggerTooltipRender(data, isFirst, this.getTooltipText(data, chart.tooltip), this.findHeader(data));
            }
        }
        else {
            if (!data.point && this.isRemove) {
                this.removeTooltip(this.accumulation.tooltip.fadeOutDuration);
                this.isRemove = false;
            }
        }
    }
    triggerTooltipRender(point, isFirst, textCollection, headerText) {
        //let template: string;
        const tooltip = this.chart.tooltip;
        const argsData = {
            cancel: false, name: tooltipRender, text: textCollection, point: point.point, textStyle: this.textStyle,
            series: point.series, headerText: headerText,
            data: {
                pointX: point.point.x, pointY: point.point.y, seriesIndex: point.series.index,
                pointIndex: point.point.index, pointText: point.point.text, seriesName: point.series.name
            }
        };
        const tooltipSuccess = (argsData) => {
            if (!argsData.cancel) {
                this.formattedText = this.formattedText.concat(argsData.text);
                this.text = this.formattedText;
                this.headerText = argsData.headerText;
                this.createTooltip(this.chart, isFirst, { x: (tooltip.location.x !== null) ? tooltip.location.x :
                        point.point.symbolLocation.x, y: (tooltip.location.y !== null) ? tooltip.location.y : point.point.symbolLocation.y }, point.series.clipRect, point.point, !tooltip.enableMarker ? [] : ['Circle'], 0, this.chart.initialClipRect, false, null, point.point, this.template ? this.accumulation.enableHtmlSanitizer ? this.accumulation.sanitize(this.template) : argsData.template : '');
            }
            else {
                this.removeHighlight();
                remove(this.getElement(this.element.id + '_tooltip'));
            }
            this.isRemove = true;
        };
        tooltipSuccess.bind(this, point);
        this.chart.trigger(tooltipRender, argsData, tooltipSuccess);
    }
    getPieData(e, chart) {
        const target = e.target;
        const id = indexFinder(target.id, true);
        if (!isNaN(id.series)) {
            const seriesIndex = id.series;
            const pointIndex = id.point;
            if (!isNullOrUndefined(seriesIndex) && !isNaN(seriesIndex) && !isNullOrUndefined(pointIndex) && !isNaN(pointIndex)) {
                const series = this.getSeriesFromIndex(seriesIndex, chart.visibleSeries);
                if (series.enableTooltip) {
                    return new AccPointData(series.points[pointIndex], series);
                }
            }
        }
        return new AccPointData(null, null);
    }
    /**
     * To get series from index.
     *
     * @param {number} index - The index of the series to retrieve.
     * @param {AccumulationSeries[]} visibleSeries - The array of visible series in the accumulation chart.
     * @returns {AccumulationSeries} - The series retrieved from the specified index.
     */
    getSeriesFromIndex(index, visibleSeries) {
        return visibleSeries[index];
    }
    getTooltipText(data, tooltip) {
        const series = data.series;
        let format = tooltip.format ? tooltip.format : this.accumulation.theme.indexOf('Tailwind3') > -1 ? '${point.x} : ${point.y}' : '${point.x} : <b>${point.y}</b>';
        format = this.accumulation.useGroupingSeparator ? format.replace('${point.y}', '${point.separatorY}') : format;
        return this.parseTemplate(data.point, series, format);
    }
    findHeader(data) {
        if (this.header === '') {
            return '';
        }
        this.header = this.parseTemplate(data.point, data.series, this.header);
        if (this.header.replace(/<b>/g, '').replace(/<\/b>/g, '').trim() !== '') {
            return this.header;
        }
        return '';
    }
    /**
     * Parses the tooltip template string and resolves all supported point and series tokens.
     *
     * @param {AccPoints} point - The accumulation chart point used to resolve point-based tooltip tokens.
     * @param {AccumulationSeries} series - The accumulation chart series used to resolve series-based tooltip tokens.
     * @param {string} format - The tooltip template format string.
     * @returns {string} - Returns the parsed tooltip text with resolved token values.
     * @private
     */
    parseTemplate(point, series, format) {
        const pointKeys = Object.keys(point);
        const seriesKeys = Object.keys(Object.getPrototypeOf(series));
        if (format.indexOf('${') !== -1) {
            let previous;
            do {
                previous = format;
                format = this.resolveTooltipTokens(format, point, series, pointKeys, seriesKeys);
            } while (format !== previous && format.indexOf('${') !== -1);
        }
        return format;
    }
    /**
     * Resolves tooltip tokens in the specified template string.
     *
     * @param {string} input - The tooltip template string that contains tokens to resolve.
     * @param {AccPoints} point - The accumulation chart point used to resolve point-based tokens.
     * @param {AccumulationSeries} series - The accumulation chart series used to resolve series-based tokens.
     * @param {string[]} pointKeys - The available point property keys.
     * @param {string[]} seriesKeys - The available series property keys.
     * @returns {string} - Returns the tooltip template string with supported tokens resolved.
     * @private
     */
    resolveTooltipTokens(input, point, series, pointKeys, seriesKeys) {
        return input.replace(/\$\{([^{}]+)\}/g, this.replaceTooltipToken.bind(this, point, series, pointKeys, seriesKeys));
    }
    /**
     * Replaces a single tooltip token with the corresponding point or series value.
     *
     * @param {AccPoints} point - The accumulation chart point used to resolve point-based tokens.
     * @param {AccumulationSeries} series - The accumulation chart series used to resolve series-based tokens.
     * @param {string[]} pointKeys - The available point property keys.
     * @param {string[]} seriesKeys - The available series property keys.
     * @param {string} match - The complete matched tooltip token text.
     * @param {string} token - The token content inside the tooltip expression.
     * @returns {string} - Returns the resolved tooltip token value, or the original token text when it cannot be resolved.
     * @private
     */
    replaceTooltipToken(point, series, pointKeys, seriesKeys, match, token) {
        const colonIndex = token.indexOf(':');
        let rawPath;
        let inlineFormat = '';
        if (colonIndex === -1) {
            rawPath = token;
        }
        else {
            rawPath = token.substring(0, colonIndex);
            inlineFormat = token.substring(colonIndex + 1).trim();
        }
        // Path should not contain leading, trailing, or internal whitespace.
        // Format can contain whitespace, for example: ${point.x:MMM dd}
        if (rawPath !== rawPath.trim() || /\s/.test(rawPath)) {
            return match;
        }
        const path = rawPath;
        let rawValue;
        let fallbackText = '';
        if (path.indexOf('point.') === 0) {
            const key = path.slice(6);
            // Path does not exist, so keep original token text.
            if (pointKeys.indexOf(key) === -1) {
                return match;
            }
            rawValue = point[key];
            if (isNullOrUndefined(rawValue)) {
                return String(rawValue);
            }
            if (typeof rawValue === 'number' && (isNaN(rawValue) || !isFinite(rawValue))) {
                return isNaN(rawValue) ? 'NaN' : rawValue < 0 ? '-Infinity' : 'Infinity';
            }
            fallbackText = this.getPointRawText(key, rawValue);
        }
        else if (path.indexOf('series.') === 0) {
            const key = path.slice(7);
            // Path does not exist, so keep original token text.
            if (seriesKeys.indexOf(key) === -1) {
                return match;
            }
            rawValue = series[key];
            if (isNullOrUndefined(rawValue)) {
                return String(rawValue);
            }
            fallbackText = String(rawValue);
        }
        else {
            return match;
        }
        // No inline format, so return the normal template value directly.
        if (colonIndex === -1 || inlineFormat === '') {
            return fallbackText;
        }
        return this.getFormattedTooltipValue(rawValue, inlineFormat, fallbackText, this.accumulation.intl, this.accumulation.useGroupingSeparator);
    }
    /**
     * Gets the raw tooltip text for the specified point value.
     *
     * @param {string} key - The point property key.
     * @param {any} rawValue - The raw point value.
     * @returns {string} - Returns the raw point value as tooltip text.
     * @private
     */
    getPointRawText(key, rawValue) {
        if (key === 'x' &&
            this.accumulation.useGroupingSeparator &&
            typeof rawValue === 'number') {
            return this.accumulation.intl.formatNumber(rawValue, { useGrouping: true });
        }
        return String(rawValue);
    }
    /**
     * Get module name.
     *
     * @returns {string} - Returns the module name.
     */
    getModuleName() {
        return 'AccumulationTooltip';
    }
    /**
     * To destroy the Tooltip.
     *
     * @returns {void}
     * @private
     */
    destroy() {
        /**
         * Destroy method calling here
         */
    }
}
